import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useNavigate, useLocation } from 'react-router-dom'; // ✅ useLocation adicionado para o timer de 1h
import '../styles/index.css';

// 👇 IMPORTAÇÃO DOS ALERTAS PADRONIZADOS E MODAL
import { showSuccess, showError, showWarning } from '../utils/alertas'; 
import TrocaSenhaModal from '../components/TrocaSenhaModal';

// --- 🛠️ CONFIGURAÇÕES DE AMBIENTE ---
const IS_PROD = true; 

const API_BASE_URL = IS_PROD 
    ? 'https://sigoapi.jhe-etes.com.br/api' 
    : 'http://localhost:3001/api'; 

const COLOR_DEFAULT = '#64748b'; // Cinza padrão

// --- 1. FUNÇÃO ÍCONE (PIN) DINÂMICO ---
const getPinIcon = (color) => {
  const pinColor = color || COLOR_DEFAULT;
  const svgTemplate = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" width="28" height="30">
      <filter id="shadow"><feDropShadow dx="0" dy="2" stdDeviation="1.5" flood-color="#00000033"/></filter>
      <path fill="${pinColor}" stroke="#ffffff" stroke-width="10" filter="url(#shadow)" d="M384 192c0 87.4-117 243-168.3 307.2c-12.3 15.3-35.1 15.3-47.4 0C117 435 0 279.4 0 192C0 86 86 0 192 0s192 86 192 192z"/>
      <circle cx="192" cy="192" r="64" fill="#ffffff"/>
    </svg>
  `;
  return L.divIcon({ 
    className: 'custom-svg-pin', 
    html: svgTemplate, 
    iconSize: [32, 42], 
    iconAnchor: [16, 42], 
    popupAnchor: [0, -42] 
  });
};

// --- 2. COMPONENTE DE SELECT PERSONALIZADO ---
const CustomSelect = ({ label, value, onChange, options, defaultText }) => (
  <div className="mb-4">
    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 ml-1">{label}</label>
    <div className="relative">
      <select 
        value={value} 
        onChange={onChange} 
        className="w-full p-3 pl-4 pr-10 bg-white border-2 border-gray-100 text-gray-700 text-sm rounded-xl appearance-none focus:outline-none focus:border-sabesp-cyan/50 focus:ring-2 focus:ring-sabesp-cyan/20 transition-all cursor-pointer shadow-sm hover:border-gray-200"
        style={{ 
            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, 
            backgroundPosition: `right 0.75rem center`, 
            backgroundRepeat: `no-repeat`, 
            backgroundSize: `1.5em 1.5em` 
        }}
      >
        <option value="Todos">{defaultText}</option>
        {options.map((opt, idx) => (
            <option key={idx} value={opt.nome || opt}>
                {opt.label || opt.nome || opt}
            </option>
        ))}
      </select>
    </div>
  </div>
);

function ResetMap({ center }) {
  const map = useMap();
  useEffect(() => { map.setView(center, 10); }, [center, map]);
  return null;
}

function Mapa() {
  const navigate = useNavigate();
  const location = useLocation(); // ✅ Necessário para o App.js monitorar inatividade
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSenhaModalOpen, setIsSenhaModalOpen] = useState(false);

  // 🔐 Auth 
  const token = localStorage.getItem('token');
  const isLoggedIn = !!token;

  // Dados
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [listaGerencias, setListaGerencias] = useState([]); 
  
  // Filtros
  const [busca, setBusca] = useState('');
  const [filtroMunicipio, setFiltroMunicipio] = useState('Todos');
  const [filtroGerencia, setFiltroGerencia] = useState('Todos');

  // 🔥 LÓGICA: MUNICÍPIOS DINÂMICOS
  const municipiosComObras = useMemo(() => {
      const nomes = empreendimentos
          .map(emp => emp.nome_municipio) 
          .filter(nome => nome && nome.trim() !== ""); 
      return [...new Set(nomes)].sort();
  }, [empreendimentos]);

  const handleAuthAction = async () => { 
    if (isLoggedIn) {
        if (!window.confirm("Deseja realmente sair do sistema?")) return;
        
        try {
            await fetch(`${API_BASE_URL}/logout`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
        } catch (error) { console.error("Erro logout:", error); }

        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.reload(); 
    } else {
        navigate('/login');
    }
  };

  useEffect(() => {
    async function carregarListas() {
      try {
        const respGer = await fetch(`${API_BASE_URL}/gerencias`);
        if (respGer.ok) {
            const gerRaw = await respGer.json();
            const gerFormatada = gerRaw.map(g => ({
                ...g,
                label: `${g.nome} – ${g.responsavel || 'Sem Responsável'}`
            }));
            setListaGerencias(gerFormatada);
        }
      } catch (error) { console.error("Erro ao carregar listas:", error); }
    }
    carregarListas();
  }, []);

  useEffect(() => {
    const fetchDados = async () => {
        const params = new URLSearchParams();
        if (busca.trim()) params.append('busca', busca);
        if (filtroMunicipio !== 'Todos') params.append('municipio', filtroMunicipio);
        if (filtroGerencia !== 'Todos') params.append('gerencia', filtroGerencia);
        
        const cacheKey = `sigo_cache_${params.toString()}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) setEmpreendimentos(JSON.parse(cachedData));

        try {
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;

            const response = await fetch(`${API_BASE_URL}/empreendimentos?${params.toString()}`, {
                method: 'GET',
                headers: headers
            });

            if (response.ok) {
                const data = await response.json();
                if (JSON.stringify(data) !== cachedData) {
                    setEmpreendimentos(data);
                    sessionStorage.setItem(cacheKey, JSON.stringify(data));
                }
            }
        } catch (error) { console.error("Erro na busca:", error); }
    };

    const delayDebounceFn = setTimeout(() => { fetchDados(); }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [busca, filtroMunicipio, filtroGerencia, token]); 

  const limparFiltros = () => {
      setFiltroMunicipio('Todos'); setFiltroGerencia('Todos'); setBusca('');
  };

  return (
    <div className="flex h-screen w-full font-sans bg-slate-50 overflow-hidden relative">
      {isSidebarOpen && (<div className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm" onClick={() => setIsSidebarOpen(false)}></div>)}

      <aside className={`fixed inset-y-0 left-0 z-40 w-80 h-full flex flex-col shadow-2xl bg-white/95 backdrop-blur-xl border-r border-white/20 transform transition-transform duration-300 md:relative md:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 pb-4 flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2 mb-1"><span className="text-2xl">💧</span><h1 className="text-2xl font-extrabold text-sabesp-blue tracking-tight">SIGO Sabesp</h1></div>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest ml-9">Painel de Obras</p>
          </div>
          <button onClick={() => setIsSidebarOpen(false)} className="md:hidden p-2 text-gray-400">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
            <div className="mb-6">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 ml-1">Pesquisa Rápida</label>
                <div className="relative">
                    <input type="text" placeholder="Nome, contrato ou cidade..." value={busca} onChange={(e) => setBusca(e.target.value)} className="w-full p-3 pl-10 bg-white border-2 border-blue-50 rounded-xl text-sm focus:outline-none focus:border-sabesp-cyan transition-all shadow-sm" />
                    <span className="absolute left-3 top-3 text-gray-300 text-lg">🔍</span>
                </div>
            </div>

            <div className="space-y-2 mb-8">
                <CustomSelect label="Gerência" value={filtroGerencia} onChange={(e) => setFiltroGerencia(e.target.value)} options={listaGerencias} defaultText="Todas as gerências" />
                <CustomSelect label="Município" value={filtroMunicipio} onChange={(e) => setFiltroMunicipio(e.target.value)} options={municipiosComObras} defaultText="Todos os municípios" />
            </div>

            <button onClick={limparFiltros} className="w-full py-3 bg-blue-50 text-sabesp-blue hover:bg-sabesp-blue hover:text-white rounded-xl text-sm font-bold transition-all duration-300 shadow-sm flex items-center justify-center gap-2 mb-8 group">
                <span className="group-hover:rotate-12 transition-transform">🧹</span> Limpar Filtros
            </button>
        </div>
        
        {/* 👇 RODAPÉ ORIGINAL DISCRETO PRESERVADO */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 text-center flex flex-col gap-3">
            <p className="text-[11px] font-bold text-gray-400 uppercase">Exibindo <span className="text-sabesp-blue text-sm">{empreendimentos.length}</span> ETES</p>
            
            {isLoggedIn && (
                <button 
                    onClick={() => setIsSenhaModalOpen(true)}
                    className="text-[10px] font-bold text-gray-500 hover:text-sabesp-blue uppercase tracking-widest py-1 px-3 border border-transparent hover:border-blue-100 rounded-full transition-all flex items-center justify-center gap-1 mx-auto"
                >
                    🔒 Alterar Senha
                </button>
            )}

            <button onClick={handleAuthAction} className={`text-[10px] font-bold uppercase tracking-widest py-1 px-3 rounded-full transition-all w-fit mx-auto ${isLoggedIn ? 'text-red-400 hover:bg-red-50' : 'text-gray-300 hover:text-sabesp-blue hover:bg-blue-50'}`}>
                {isLoggedIn ? '🔓 Sair do Sistema' : '🔒 Acesso Restrito'}
            </button>
        </div>
      </aside>

      <main className="flex-grow relative z-0 w-full h-full">
        <button onClick={() => setIsSidebarOpen(true)} className="md:hidden absolute top-4 left-4 z-[1000] bg-white text-sabesp-blue px-4 py-2.5 rounded-xl shadow-xl font-bold flex items-center gap-2 text-sm">☰ Filtros</button>

        <MapContainer center={[-23.55052, -46.63330]} zoom={10} style={{ height: "100%", width: "100%" }} zoomControl={false}>
            <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
            <ResetMap center={[-23.55052, -46.63330]} />

            {empreendimentos.map((emp) => {
                const corFinal = emp.cor || COLOR_DEFAULT;
                const dynamicIcon = getPinIcon(corFinal);

                return (
                <Marker key={emp.id} position={emp.coordenadas} icon={dynamicIcon}>
                    <Tooltip direction="top" offset={[0, -40]} opacity={1}><span className="font-bold text-xs">{emp.nome}</span></Tooltip>
                    
                    <Popup minWidth={280} maxWidth={320} className="custom-popup">
                        <div className="p-1 font-sans">
                            <div className="border-b border-gray-100 pb-2 mb-2">
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-1">{emp.nome_gerencia}</span>
                                <h3 className="font-black text-sabesp-blue text-base uppercase leading-tight">{emp.nome}</h3>
                                <p className="text-xs text-gray-500 font-medium mt-1">📍 {emp.nome_municipio}</p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 mb-4 bg-gray-50 p-2 rounded-lg border border-gray-100">
                                <div className="text-center border-r border-gray-200 pr-1">
                                    <span className="text-[9px] text-gray-400 uppercase font-bold block">Vazão Atual</span>
                                    <span className="text-sm font-bold text-gray-700">{emp.vazao_atual_ls || '-'} L/s</span>
                                </div>
                                <div className="text-center">
                                    <span className="text-[9px] text-gray-400 uppercase font-bold block">Vazão Futura</span>
                                    <span className="font-bold text-sabesp-blue text-sm">{emp.vazao_futura_ls || '-'} L/s</span>
                                </div>
                            </div>

                            {/* 👇 BLOCO DE CONTRATOS PRESERVADO INTEGRALMENTE */}
                            {emp.contratos && emp.contratos.length > 0 && (
                                <div className="mb-4 border-t border-gray-100 pt-3">
                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest block mb-2">Contratos Vinculados</span>
                                    <div className="space-y-2 max-h-32 overflow-y-auto custom-scrollbar pr-1">
                                            {emp.contratos.map((c, idx) => (
                                                <div key={idx} className="flex justify-between items-start text-[11px] bg-gray-50 p-2 rounded border border-gray-100">
                                                    <div>
                                                        <span className="font-bold text-gray-800 block">{c.codigo}</span>
                                                        <span className="text-[10px] text-gray-500 leading-tight block">{c.nome}</span>
                                                    </div>
                                                    <span 
                                                        className="text-[9px] font-bold text-white px-1.5 py-0.5 rounded uppercase tracking-wider ml-2 whitespace-nowrap"
                                                        style={{ backgroundColor: c.cor || '#ccc' }}
                                                    >
                                                        {c.status}
                                                    </span>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            )}

                            <button onClick={() => navigate(`/empreendimento/${emp.id}`)} className="w-full py-2.5 bg-sabesp-cyan text-white text-xs font-bold rounded-lg hover:bg-cyan-600 transition-all shadow-md uppercase tracking-wider">Ver Detalhes Completos</button>
                        </div>
                    </Popup>
                </Marker>
            )})}
        </MapContainer>
      </main>

      <TrocaSenhaModal 
          isOpen={isSenhaModalOpen} 
          onClose={() => setIsSenhaModalOpen(false)} 
      />
    </div>
  );
}

// Estilos mantidos originais
const styles = `.custom-scrollbar::-webkit-scrollbar { width: 4px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 10px; } .leaflet-popup-content-wrapper { border-radius: 16px; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1); } .leaflet-popup-content { margin: 12px; }`;
const styleTag = document.createElement("style");
styleTag.innerHTML = styles;
document.head.appendChild(styleTag);

export default Mapa;